"use client";
import { createSession } from "@/app/[locale]/auth/login/loginActions";
import type { ErrorKeys, FormState } from "@/app/[locale]/auth/login/types";
import { useAnalyticsEvents } from "@/hooks/useAnalyticsEvents";
import { getZendeskSupportUrl } from "@/modules/support/utils";
import {
  Button,
  Checkbox,
  Divider,
  Hint,
  Input,
  SvgAttentionOutlined,
  SvgInProgressAnimated,
  SvgQuestionMarkOutlined,
  SvgStars,
  Text,
} from "@getgrover/ui";
import { useLocale, useTranslations } from "next-intl";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useActionState, useEffect } from "react";
import {
  AnimatedIconWrapper,
  Container,
  FormContainer,
  StyledHeader,
  StyledLink,
} from "./styles";

const ORIGIN_QUERY_PARAM = "o";

export const LoginScreen = () => {
  const t = useTranslations("auth.login");
  const [email, setEmail] = useQueryState("email");
  const [rememberMe, setRememberMe] = useQueryState(
    "rememberMe",
    parseAsBoolean.withDefault(false),
  );
  const [traceId] = useQueryState("traceId");
  const [origin] = useQueryState(ORIGIN_QUERY_PARAM);
  const { sendAnalyticsEvent } = useAnalyticsEvents();
  const locale = useLocale();
  const zendeskUrl = getZendeskSupportUrl(locale);

  const [state, formAction, isLoading] = useActionState(
    async (_state: FormState, formData: FormData) =>
      await createSession(formData),
    null,
  );

  useEffect(() => {
    if (traceId || state?.errors?.general) {
      sendAnalyticsEvent("Partner Login", {
        status: "failed",
        reason: state?.errors?.general || { traceId },
      });
    }
  }, [state?.errors, traceId, sendAnalyticsEvent]);

  return (
    <Container>
      <FormContainer action={formAction}>
        {origin === "legacy" && (
          <Hint icon={<SvgStars />}>
            <Text $block={true} $typography="RegularParagraphBold">
              {t("migration.title")}
            </Text>
            <Text $block={true} as="p" $typography="SmallParagraph">
              {t.rich("migration.description", {
                sharepoint: (chunks) => (
                  <StyledLink
                    href="https://mediasaturn.sharepoint.com/sites/deu-financial-and-3rd-party-services/SitePages/Grover%20-%20Produkte%20mieten.aspx#zugang-zur-anwendung"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunks}
                  </StyledLink>
                ),
                video: (chunks) => (
                  <StyledLink
                    href="https://www.youtube.com/watch?v=BXk5e2FNJQw"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunks}
                  </StyledLink>
                ),
              })}
            </Text>
          </Hint>
        )}
        <StyledHeader>{t("title")}</StyledHeader>
        <Input
          name="email"
          value={email || ""}
          autoComplete="email"
          inputMode="email"
          label={t("email")}
          onChange={(value: string) => setEmail(value)}
          error={
            state?.errors?.email?.[0] ? (
              <Text as="span">{t(state?.errors?.email[0] as ErrorKeys)}</Text>
            ) : null
          }
        />

        <Checkbox
          boxed={false}
          value={rememberMe}
          defaultChecked={rememberMe}
          label={t("remember")}
          labelPlacement="end"
          name="rememberMe"
          onChange={(checked) => setRememberMe(checked)}
        />
        {state?.errors?.general ? (
          <Hint icon={<SvgAttentionOutlined />} variant="error">
            {t(state?.errors?.general as ErrorKeys)}
          </Hint>
        ) : null}
        {traceId ? (
          <Hint variant="error">
            <Text $block={true}>{t("error.internal_server")}</Text>
            <Text $block={true} $typography="TinyParagraph" $color="darkGrey">
              Trace ID: {traceId}
            </Text>
          </Hint>
        ) : null}
        <Button
          fullWidth
          variant="Red"
          disabled={isLoading}
          onClick={() =>
            sendAnalyticsEvent("Partner Login", {
              status: "started",
            })
          }
        >
          {isLoading && (
            <AnimatedIconWrapper>
              <SvgInProgressAnimated />
            </AnimatedIconWrapper>
          )}
          {t("submit")}
        </Button>
        <Divider>{t("divider")}</Divider>
      </FormContainer>
      <Button
        href={zendeskUrl}
        target="_blank"
        fullWidth
        onClick={() =>
          sendAnalyticsEvent("Support Contacted", {
            position: "login",
            support_method: "help-desk",
          })
        }
        variant="SecondaryOutlined"
      >
        <SvgQuestionMarkOutlined />
        {t("support")}
      </Button>
    </Container>
  );
};
